// src/lib/db/search-pattern.ts
//
// Storefront search matches Arabic spelling variants as the same word:
// shoppers type "كنبه" for "كنبة", "اريكة" for "أريكة", "كرسى" for "كرسي",
// often from keyboards that also produce the Persian ک / ی. ILIKE can't
// fold those, and normalising the columns would need a migration, so the
// query becomes a case-insensitive regex (PostgREST `imatch`, Postgres ~*)
// in which each variant letter is a character class of its group.

/** Letters shoppers use interchangeably; each matches its whole group. */
const VARIANT_GROUPS = ["اأإآٱ", "ةه", "يىئی", "وؤ", "كک"];

/** Harakat, superscript alef and tatweel: dropped from the query, optional in the text. */
const MARKS = "\u064B-\u0652\u0670\u0640";
const MARKS_RE = new RegExp(`[${MARKS}]`, "g");
const OPTIONAL_MARKS = `[${MARKS}]*`;

const REGEX_META = /[\\^$.|?*+()[\]{}]/;
const ARABIC_LETTER = /[\u0621-\u064A\u0671-\u06D3]/;

/**
 * The query with diacritics and tatweel removed — what the pattern is built
 * from. Empty means there is nothing to search for (callers return no
 * results rather than a pattern that matches every row).
 */
export function normalizeSearchQuery(value: string): string {
  return value.replace(MARKS_RE, "").trim();
}

/**
 * A user-typed term as a quoted PostgREST `or()` value for `imatch`,
 * matching it anywhere in the text with Arabic spelling variants folded.
 * Regex metacharacters are escaped so they match themselves, then the
 * result is double-quoted for PostgREST with `\` and `"` escaped — the same
 * quoting toSafeIlikePattern uses, for the same reason (`,` and `()` in a
 * raw value would change the or() grammar).
 */
export function toSafeSearchRegex(value: string): string {
  const regex = [...normalizeSearchQuery(value)]
    .map((ch) => {
      const group = VARIANT_GROUPS.find((g) => g.includes(ch));
      const token = group ? `[${group}]` : REGEX_META.test(ch) ? `\\${ch}` : ch;
      // Marks only ever follow an Arabic letter, so only those get the gap.
      return ARABIC_LETTER.test(ch) ? token + OPTIONAL_MARKS : token;
    })
    .join("");
  const quoted = regex.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `"${quoted}"`;
}

/** `col.imatch.<pattern>` for each column, joined for an or() filter. */
export function buildSearchOrFilter(columns: string[], value: string): string {
  const pattern = toSafeSearchRegex(value);
  return columns.map((c) => `${c}.imatch.${pattern}`).join(",");
}
